import { api, socket } from './api';

/**
 * WebRTC audio call (callee side). Signaling travels over the realtime socket; media is peer-to-peer
 * (or relayed by TURN when configured).
 */
export class VoiceCall {
  private pc: RTCPeerConnection | null = null;
  private stream: MediaStream | null = null;
  private off: Array<() => void> = [];
  readonly audio = new Audio();
  private pendingOffer: RTCSessionDescriptionInit | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];

  constructor(
    readonly sessionId: string,
    private readonly iceServers: RTCIceServer[],
    private readonly onEnd: () => void,
  ) {
    this.audio.autoplay = true;
    this.off.push(
      socket.on('voice.signal', async (m) => {
        if (m.sessionId !== this.sessionId) return;
        if (m.data.type === 'offer') {
          this.pendingOffer = { type: 'offer', sdp: m.data.sdp };
          if (this.pc) await this.answerOffer();
        } else if (m.data.type === 'candidate' && m.data.candidate) {
          if (this.pc?.remoteDescription) await this.pc.addIceCandidate(m.data.candidate).catch(() => undefined);
          else this.pendingCandidates.push(m.data.candidate);
        }
      }),
      socket.on('voice.ended', (m) => m.sessionId === this.sessionId && this.cleanup()),
    );
  }

  async accept() {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    this.pc = new RTCPeerConnection({ iceServers: this.iceServers });
    this.stream.getTracks().forEach((t) => this.pc!.addTrack(t, this.stream!));
    this.pc.ontrack = (e) => {
      this.audio.srcObject = e.streams[0] ?? null;
      void this.audio.play().catch(() => undefined);
    };
    this.pc.onicecandidate = (e) => e.candidate && socket.send({ type: 'voice.signal', sessionId: this.sessionId, data: { type: 'candidate', candidate: e.candidate.toJSON() } });
    this.pc.onconnectionstatechange = () => ['failed', 'closed'].includes(this.pc?.connectionState ?? '') && void this.hangup();
    await api.post(`/driver/voice/${this.sessionId}/accept`);
    if (this.pendingOffer) await this.answerOffer();
  }

  private async answerOffer() {
    if (!this.pc || !this.pendingOffer) return;
    await this.pc.setRemoteDescription(this.pendingOffer);
    this.pendingOffer = null;
    for (const c of this.pendingCandidates.splice(0)) await this.pc.addIceCandidate(c).catch(() => undefined);
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    socket.send({ type: 'voice.signal', sessionId: this.sessionId, data: { type: 'answer', sdp: answer.sdp } });
  }

  async reject() {
    await api.post(`/driver/voice/${this.sessionId}/reject`).catch(() => undefined);
    this.cleanup();
  }

  async hangup() {
    await api.post(`/driver/voice/${this.sessionId}/hangup`).catch(() => undefined);
    this.cleanup();
  }

  private cleanup() {
    this.off.forEach((f) => f());
    this.off = [];
    this.stream?.getTracks().forEach((t) => t.stop());
    this.pc?.close();
    this.pc = null;
    this.onEnd();
  }
}
